import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Standardized Empty State Component
 * 
 * Use this component for consistent empty state messaging across all dashboard components.
 * Replaces inconsistent empty state implementations throughout the codebase.
 */

interface EmptyStateProps {
  /** Primary message to display */
  message?: string
  /** Optional secondary hint text */
  hint?: string
  /** Optional icon to display above the message */
  icon?: React.ReactNode
  /** Custom className for additional styling */
  className?: string
  /** Variant for different contexts */
  variant?: 'default' | 'compact' | 'card'
}

export function EmptyState({
  message = 'No data to display',
  hint = 'Try a different date range.',
  icon,
  className,
  variant = 'default',
}: EmptyStateProps) {
  const variantStyles = {
    default: 'py-8 gap-2',
    compact: 'py-4 gap-1.5',
    card: 'py-12 gap-3',
  }

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center w-full flex-1",
        variantStyles[variant],
        className
      )}
    >
      {icon && (
        <div className="text-muted-foreground/40 mb-2">
          {icon}
        </div>
      )}
      <span className="text-[13px] text-muted-foreground/60">
        {message}
      </span>
      {hint && (
        <span className="text-[11px] text-muted-foreground/40">
          {hint}
        </span>
      )}
    </div>
  )
}

/**
 * Preset empty states for common scenarios
 */
export function NoDataEmptyState({ className }: { className?: string }) {
  return (
    <EmptyState
      message="No data to display"
      hint="Try a different date range."
      className={className}
    />
  )
}

export function LoadingDataEmptyState({ className }: { className?: string }) {
  return (
    <EmptyState
      message="Loading data..."
      hint="Please wait while we fetch your analytics."
      className={className}
    />
  )
}

export function NoTrafficEmptyState({ className }: { className?: string }) {
  return (
    <EmptyState
      message="No traffic data available"
      hint="Data will appear as visitors interact with your site."
      className={className}
    />
  )
}

export function NoChartDataEmptyState({ className }: { className?: string }) {
  return (
    <EmptyState
      message="No chart data available"
      hint="Select a different time period or check your filters."
      className={className}
    />
  )
}

export default EmptyState

